import React, { useMemo, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Stat, SupplierCard, Input, Empty, Pill } from '../ui';
import {
  dashboardStats,
  matchesSearch,
  capturedToday,
  quotesExpiring,
  revisitList,
  budgetLine,
  planProgress,
  checklistProgress,
  supplierDecision,
  money,
} from '../rules';
import { p, BASE } from '../routes';
import { tr } from '../i18n';

/**
 * Dashboard (spec § 5.3) — the current fair.
 *
 * Six stat tiles that double as filters, the big blue capture button, the Today
 * card, quick links and global search. Everything here reads the local
 * database, so it renders identically offline.
 */
export default function DashboardScreen() {
  const { fairs, fair, visibleSuppliers, currentFair, welcomed } = useFairTracker();
  const [query, setQuery] = useState('');

  // Hooks must run before any early return, or their order changes between
  // renders once a guard fires (react-hooks/rules-of-hooks).
  const results = useMemo(
    () => (query.trim() ? visibleSuppliers.filter((s) => matchesSearch(s, query)) : null),
    [query, visibleSuppliers],
  );

  const recent = useMemo(
    () => [...visibleSuppliers].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 6),
    [visibleSuppliers],
  );

  // First run, or a fair was deleted out from under us.
  if (!welcomed && !fairs.length) return <Redirect to={p('/welcome')} />;
  if (!fairs.length) return <Redirect to={p('/fairs')} />;
  if (!currentFair) return <Redirect to={p('/fairs')} />;

  const allFairs = currentFair === 'all';
  const stats = dashboardStats(visibleSuppliers);
  const today = capturedToday(visibleSuppliers);
  const expiring = quotesExpiring(visibleSuppliers);
  const revisits = revisitList(visibleSuppliers);
  const budget = budgetLine(visibleSuppliers, fair);
  const plan = planProgress(fair);
  const checks = checklistProgress(fair);

  const fairNameFor = (s) => {
    if (!allFairs) return null;
    const f = fairs.find((x) => x.id === s.fairId);
    return f ? f.name : null;
  };

  const todayStrong = today.filter((s) => supplierDecision(s) === 'Strong').length;
  const showToday = today.length > 0 || revisits.length > 0 || expiring.length > 0;

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{allFairs ? 'All my fairs' : fair ? fair.name : 'Dashboard'}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {allFairs
            ? `${fairs.length} fairs · ${visibleSuppliers.length} suppliers`
            : [fair && fair.location, fair && fair.dates].filter(Boolean).join(' · ') || tr('No dates set')}
        </p>
      </div>

      <div className="ft-stats">
        <Stat label={tr("Suppliers")} value={stats.total} to={p('/suppliers')} />
        <Stat label={tr("Strong")} value={stats.strong} tone="green" to={`${p('/suppliers')}?d=Strong`} />
        <Stat label={tr("Maybe")} value={stats.maybe} tone="amber" to={`${p('/suppliers')}?d=Maybe`} />
        <Stat label={tr("No")} value={stats.no} tone="red" to={`${p('/suppliers')}?d=No`} />
        <Stat label={tr("Samples")} value={stats.samples} to={`${p('/suppliers')}?sample=1`} />
        <Stat label={tr("Follow-ups")} value={stats.followUps} to={p('/followups')} />
      </div>

      <div className="ft-cta-row">
        <Link className="ft-btn ft-btn-primary ft-btn-block ft-btn-big" to={p('/entry')}>
          <Icon name="Plus" size={20} /> {tr("Add Supplier / Product")}
        </Link>
        <Link className="ft-btn ft-btn-ink ft-btn-block ft-btn-big" style={{ marginTop: 8 }} to={p('/card')}>
          Show my card 名片
        </Link>
      </div>

      {showToday && (
        <section className="ft-card ft-today">
          <h2 className="ft-h2">{tr("Today")}</h2>
          <p className="ft-today-line">
            {[
              today.length ? tr(`${today.length} captured`) : null,
              todayStrong ? tr(`${todayStrong} strong`) : null,
              revisits.length ? tr(`${revisits.length} to revisit`) : null,
              expiring.length ? tr(`${expiring.length} quote${expiring.length > 1 ? 's' : ''} expiring`) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <p className="ft-today-line">
            {tr("Paid at booths:")} <b>{money(budget.committed)}</b>
            {budget.budget > 0 && <> {tr('of')} {money(budget.budget)}</>}
            {budget.over && <Pill tone="no">{tr("over")}</Pill>}
          </p>
          {budget.planned > 0 && (
            <p className="ft-today-line ft-faint">
              {tr("Strong orders at MOQ ≈")} <b>{money(budget.planned)}</b>
            </p>
          )}
        </section>
      )}

      <div className="ft-quicklinks">
        <QuickLink to={p('/entry')} icon="Plus" label={tr("Quick Entry")} />
        <QuickLink to={p('/suppliers')} icon="Users" label={tr("My Suppliers")} />
        <QuickLink
          to={p('/plan')}
          icon="ClipboardList"
          label={tr("My Fair Plan")}
          badge={plan.total ? `${plan.found}/${plan.total}` : null}
        />
        <QuickLink
          to={p('/revisit')}
          icon="RotateCcw"
          label={tr("Booths to Revisit")}
          badge={revisits.length || null}
        />
        <QuickLink to={p('/card')} icon="CreditCard" label={tr("Show to supplier")} />
        <QuickLink
          to={p('/checklist')}
          icon="CheckSquare"
          label={tr("Pre-trip checklist")}
          badge={checks.total ? `${checks.done}/${checks.total}` : null}
        />
        <QuickLink to={p('/shortlist')} icon="Star" label={tr("Final Shortlist")} />
        <QuickLink to={p('/followups')} icon="Bell" label={tr("Follow-ups")} badge={stats.followUps || null} />
        <QuickLink to={p('/fairs')} icon="MapPin" label={tr("My Fairs")} />
      </div>

      <div className="ft-row" style={{ marginTop: 10 }}>
        <Link className="ft-btn" to={`${p('/settings')}?open=reports`}>
          {tr("Fair report PDF")}
        </Link>
        <Link className="ft-btn" to={`${p('/settings')}?open=backup`}>
          {tr("Backup & exports")}
        </Link>
      </div>

      <section style={{ marginTop: 18 }}>
        <Input
          value={query}
          onChange={setQuery}
          placeholder={tr("Search suppliers, contacts, products…")}
          aria-label="Search"
        />
      </section>

      {results ? (
        <section style={{ marginTop: 12 }}>
          <h2 className="ft-h2">
            {results.length} result{results.length === 1 ? '' : 's'}
          </h2>
          <div className="ft-list">
            {results.map((s) => (
              <SupplierCard key={s.id} supplier={s} fair={fair} fairName={fairNameFor(s)} />
            ))}
          </div>
          {!results.length && <Empty title={tr("Nothing matched")}>{tr("Try a booth number or a product name.")}</Empty>}
        </section>
      ) : (
        <section style={{ marginTop: 12 }}>
          <h2 className="ft-h2">{tr("Recently added")}</h2>
          {recent.length ? (
            <div className="ft-list">
              {recent.map((s) => (
                <SupplierCard key={s.id} supplier={s} fair={fair} fairName={fairNameFor(s)} />
              ))}
            </div>
          ) : (
            <Empty
              title={tr("No suppliers yet")}
              action={
                <Link className="ft-btn ft-btn-primary" to={p('/entry')}>
                  {tr("Capture the first one")}
                </Link>
              }
            >
              {tr("Quick Entry takes under two minutes per booth.")}
            </Empty>
          )}
        </section>
      )}
    </div>
  );
}

function QuickLink({ to, icon, label, badge }) {
  return (
    <Link className="ft-quicklink" to={to}>
      <Icon name={icon} size={19} />
      <span>{label}</span>
      {badge != null && <span className="ft-quicklink-badge">{badge}</span>}
    </Link>
  );
}

export { BASE };
